/**
 * ONE place where AI-supplied goal rows become goal-document rows.
 *
 * Two callers feed the same hostile input through it: the Velo drawer's
 * `apply_goals_wizard_state` / `apply_goal_kpis` tools, and the one-shot goal
 * PLAN endpoint (`fetchAiGoalPlan`). They used to be one file — the plan
 * client would have imported a page component for it — so the rules live here
 * and `AiGoalsSetupWizardTools` re-exports them for its existing importers.
 *
 * The rules mirror the server's `EventGoalService.validate`: a row that would
 * 400 the finish PUT is dropped here, not repaired.
 */
import {
  CohortTarget,
  EventGoalKpi,
  KPI_METRICS,
  KpiMetric,
  RATE_METRICS,
  ThresholdDirection,
} from "@/types/EventGoal";
import { MAX_COHORT_LABEL_LENGTH } from "@/lib/api/handlers/EventGoalHandling";
import { COHORT_AF_PREFIX, COHORT_DIMENSIONS } from "@/pages/event/goals/useCohortDimensionOptions";

export type PatchKpi = {
  metric?: unknown;
  target?: unknown;
  thresholdValue?: unknown;
  thresholdDirection?: unknown;
  deadline?: unknown;
  enabled?: unknown;
  /** COHORT_SHARE only — both required there. */
  dimension?: unknown;
  bucketValue?: unknown;
  /** COHORT_SHARE only: an after-event success measure. */
  outcome?: unknown;
  /** COHORT_SHARE only, both or neither: the group the share is taken within (W3). */
  baseDimension?: unknown;
  baseValue?: unknown;
};

export type PatchCohortTarget = {
  dimension?: unknown;
  label?: unknown;
  targetPercent?: unknown;
  /** "AT_MOST" = a cap; anything else reads "at least". */
  direction?: unknown;
};

/** A parseable ISO-8601 instant — the shape the backend deserializes `deadline`
 *  into. A free-form string ("next friday") would 400 the finish-time PUT. */
function isIsoDate(v: unknown): v is string {
  return typeof v === "string" && v !== "" && !Number.isNaN(new Date(v).getTime());
}

/**
 * A facet the backend can count: one of the built-ins or
 * `additionalFields.<name>` (mirror of `CohortDimensions.isValidDimension`;
 * the field's existence is the backend's check).
 */
export function isCohortDimension(value: string): boolean {
  return (
    (COHORT_DIMENSIONS as readonly string[]).includes(value) ||
    (value.startsWith(COHORT_AF_PREFIX) && value.length > COHORT_AF_PREFIX.length)
  );
}

/**
 * Validate + coerce one AI-supplied cohort target; null on any invalid field.
 * A `dimension` that names no facet ("C-Level", "job title") is dropped to
 * "automatic" rather than rejecting the row — the target still lands in the
 * draft, only estimated instead of counted; {@link cohortDimensionNote}
 * tells the model so. Exported for direct unit testing.
 */
export function coerceCohortTarget(row: PatchCohortTarget): CohortTarget | null {
  if (!row || typeof row !== "object") return null;
  if (typeof row.label !== "string") return null;
  const label = row.label.trim();
  if (label.length === 0 || label.length > MAX_COHORT_LABEL_LENGTH) return null;
  const targetPercent = Number(row.targetPercent);
  if (!Number.isFinite(targetPercent) || targetPercent < 0 || targetPercent > 100) return null;
  let dimension: string | undefined;
  if (row.dimension !== undefined && row.dimension !== null) {
    if (typeof row.dimension !== "string" || row.dimension.length > MAX_COHORT_LABEL_LENGTH)
      return null;
    const trimmed = row.dimension.trim();
    dimension = trimmed && isCohortDimension(trimmed) ? trimmed : undefined;
  }
  // Only a cap travels; anything else is the default "at least".
  const direction = row.direction === "AT_MOST" ? ("AT_MOST" as const) : undefined;
  return { dimension, label, targetPercent, ...(direction ? { direction } : {}) };
}

/** Validate + coerce one AI-supplied KPI row; null on any invalid field. */
/** Exported for direct unit testing. */
export function coerceKpi(row: PatchKpi): EventGoalKpi | null {
  if (!row || typeof row !== "object") return null;
  if (!KPI_METRICS.includes(row.metric as KpiMetric)) return null;
  const metric = row.metric as KpiMetric;
  const target = Number(row.target);
  const thresholdValue = Number(row.thresholdValue);
  if (!Number.isFinite(target) || target < 0) return null;
  if (!Number.isFinite(thresholdValue) || thresholdValue < 0) return null;
  if (RATE_METRICS.has(metric) && (target > 100 || thresholdValue > 100)) return null;
  if (row.thresholdDirection !== "BELOW" && row.thresholdDirection !== "ABOVE") return null;
  // A cohort share without its cohort is not a KPI — it would 400 the finish PUT.
  const isCohort = metric === "COHORT_SHARE";
  const dimension = typeof row.dimension === "string" ? row.dimension.trim() : "";
  const bucketValue = typeof row.bucketValue === "string" ? row.bucketValue.trim() : "";
  if (
    isCohort &&
    (dimension.length === 0 || bucketValue.length === 0 || bucketValue.length > MAX_COHORT_LABEL_LENGTH)
  ) {
    return null;
  }
  // Within a group (W3): both or neither, a countable facet, never the
  // measured value itself — the backend 400s anything else (kpiBaseInvalid).
  const baseDimension = typeof row.baseDimension === "string" ? row.baseDimension.trim() : "";
  const baseValue = typeof row.baseValue === "string" ? row.baseValue.trim() : "";
  if (isCohort && (baseDimension || baseValue)) {
    if (
      !baseDimension ||
      !baseValue ||
      !isCohortDimension(baseDimension) ||
      baseValue.length > MAX_COHORT_LABEL_LENGTH ||
      (baseDimension === dimension && baseValue === bucketValue)
    ) {
      return null;
    }
  }
  return {
    metric,
    target,
    thresholdValue,
    thresholdDirection: row.thresholdDirection as ThresholdDirection,
    // Drop an unparseable deadline rather than let it 400 the finish PUT.
    deadline: isIsoDate(row.deadline) ? new Date(row.deadline).toISOString() : undefined,
    enabled: row.enabled !== false,
    ...(isCohort ? { dimension, bucketValue } : {}),
    ...(isCohort && row.outcome === true ? { outcome: true } : {}),
    ...(isCohort && baseDimension && baseValue ? { baseDimension, baseValue } : {}),
  };
}
